import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add Telegram init data to requests
api.interceptors.request.use((config) => {
  const tg = window.Telegram?.WebApp;
  if (tg?.initData) {
    config.headers['X-Telegram-Init-Data'] = tg.initData;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API methods
export const apiService = {
  // Users
  getUser: (telegramId: number) => api.get(`/users/${telegramId}`),
  createUser: (data: any) => api.post('/users', data),
  updateUser: (telegramId: number, data: any) => api.put(`/users/${telegramId}`, data),
  getUserReviews: (telegramId: number, params?: any) => 
    api.get(`/users/${telegramId}/reviews`, { params }),
  getUserSubscription: (telegramId: number) => 
    api.get(`/users/${telegramId}/subscription`),
  activateSubscription: (telegramId: number, days: number, paymentId: string) =>
    api.post(`/users/${telegramId}/subscription`, { days, paymentId }),

  // Listings
  getListings: (params?: any) => api.get('/listings', { params }),
  getListing: (id: string) => api.get(`/listings/${id}`),
  createListing: (data: any) => api.post('/listings', data),
  updateListing: (id: string, data: any) => api.put(`/listings/${id}`, data),
  deleteListing: (id: string) => api.delete(`/listings/${id}`),

  // Services
  getServices: (params?: any) => api.get('/services', { params }),
  getService: (id: string) => api.get(`/services/${id}`),
  createService: (data: any) => api.post('/services', data),
  updateService: (id: string, data: any) => api.put(`/services/${id}`, data),

  // Barters
  getBarters: (params?: any) => api.get('/barters', { params }),
  getBarter: (id: string) => api.get(`/barters/${id}`),
  createBarter: (data: any) => api.post('/barters', data),
  createBarterProposal: (barterId: string, data: any) => 
    api.post(`/barters/${barterId}/proposals`, data),
  getBarterProposals: (barterId: string) => 
    api.get(`/barters/${barterId}/proposals`),
  updateProposalStatus: (barterId: string, proposalId: string, status: string) =>
    api.put(`/barters/${barterId}/proposals/${proposalId}`, { status }),

  // Auctions
  getAuctions: (params?: any) => api.get('/auctions', { params }),
  getAuction: (id: string) => api.get(`/auctions/${id}`),
  createAuction: (data: any) => api.post('/auctions', data),
  getAuctionBids: (id: string) => api.get(`/auctions/${id}/bids`),
  placeBid: (auctionId: string, data: any) => 
    api.post(`/auctions/${auctionId}/bids`, data),

  // Reviews
  createReview: (data: any) => api.post('/reviews', data),
  getUserReviewStats: (userId: number) => api.get(`/reviews/user/${userId}`),

  // Chats
  getChats: () => api.get('/chats'),
  getChat: (chatId: string) => api.get(`/chats/${chatId}`),
  createChat: (data: any) => api.post('/chats', data),
  getChatMessages: (chatId: string, params?: any) => 
    api.get(`/chats/${chatId}/messages`, { params }),
  sendMessage: (chatId: string, data: any) => 
    api.post(`/chats/${chatId}/messages`, data),
  markAsRead: (chatId: string) => api.put(`/chats/${chatId}/read`),

  // Geo
  getCountries: () => api.get('/geo/countries'),
  getRegions: (countryCode: string) => 
    api.get(`/geo/countries/${countryCode}/regions`),
  getCities: (regionId: number) => api.get(`/geo/regions/${regionId}/cities`),
  searchCities: (query: string, countryCode?: string) => 
    api.get('/geo/cities/search', { params: { q: query, countryCode } }),

  // Categories
  getCategories: (type?: 'listing' | 'service') => 
    api.get('/categories', { params: { type } }),
  getCategory: (id: string) => api.get(`/categories/${id}`),
  
  // Subscriptions
  createSubscriptionInvoice: (data: any) => api.post('/subscriptions/invoice', data),
  
  // User management
  deleteUser: (telegramId: number) => api.delete(`/users/${telegramId}`)
};
